import React, { useState } from 'react';
import { loginUsuario } from '../actions/UserActions';
import { useDispatch } from 'react-redux';
import { iniciarSesion } from '../redux/slices/sessionSlice';
import { useNavigate } from 'react-router-dom';
import { 
    TextField, 
    Box, 
    Typography,
    InputAdornment,
    IconButton,
    CircularProgress
} from '@mui/material';
import {
    Email,
    Lock,
    Visibility,
    VisibilityOff,
    Church,
    ArrowBack
} from '@mui/icons-material';
import useStyles, { capellanesColors } from '../theme/useStyles';
import logo from '../assets/logo1.png';

// Estilos para TextField (se aplican con sx)
const textFieldCapellanes = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        color: '#FFFFFF',
        '& fieldset': {
            borderColor: 'rgba(212, 175, 55, 0.4)',
            borderWidth: '2px',
        },
        '&:hover fieldset': {
            borderColor: '#D4AF37',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#D4AF37',
            borderWidth: '2px',
        },
        '&.Mui-error fieldset': {
            borderColor: '#E63946',
        },
    },
    '& .MuiInputLabel-root': {
        color: '#F4E4BC',
        '&.Mui-focused': { color: '#D4AF37' },
        '&.Mui-error': { color: '#E63946' },
    },
    '& .MuiInputBase-input': { color: '#FFFFFF' },
    '& .MuiFormHelperText-root': { color: '#E63946' },
};

const Login = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const validateForm = () => {
        const newErrors = {};
        if (!email.trim()) {
            newErrors.email = 'El email es requerido';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'El email no es válido';
        }
        if (!password.trim()) {
            newErrors.password = 'La contraseña es requerida';
        } else if (password.length < 6) {
            newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setErrors({});
        if (!validateForm()) return;

        setLoading(true);
        try {
            const response = await loginUsuario({ email, password });

            if (response.status === 200) {
                const data = response.data;
                const sessionData = {
                    username: data.userName || data.username || data.email,
                    email: data.email,
                    id: data.id,
                    token: data.token,
                    nombre: data.nombre,
                    apellido: data.apellido,
                    roles: data.roles || [],
                    admin: data.admin || false,
                    areas: data.areas || [],
                    phoneNumber: data.phoneNumber,
                    imagen: data.imagen,
                    tipoDocumento: data.tipoDocumento,
                    documento: data.documento,
                    pase: data.pase,
                    categoria: data.categoria,
                    nivelAcademico: data.nivelAcademico,
                    certificaciones: data.certificaciones,
                    cursos: data.cursos,
                    contacto1: data.contacto1,
                    parentescoC1: data.parentescoC1,
                    celularC1: data.celularC1,
                    contacto2: data.contacto2,
                    parentescoC2: data.parentescoC2,
                    celularC2: data.celularC2,
                    lockoutEnabled: data.lockoutEnabled,
                    esBloqueado: data.esBloqueado,
                    twoFactorEnabled: data.twoFactorEnabled || false,
                };
                dispatch(iniciarSesion(sessionData));
                navigate('/');
            } else {
                setErrors({ general: response.data?.message || 'Error en el login' });
            }
        } catch (err) {
            setErrors({ 
                general: err.response?.data?.message || err.message || 'No autorizado. Verifica tus credenciales.' 
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className={classes.capellanesContainer}>
            {/* Overlay decorativo */}
            <Box className={classes.capellanesOverlay} />

            {/* Círculos decorativos */}
            <Box className={classes.capellanesCircleLarge} sx={{ top: '10%', left: '5%' }} />
            <Box className={classes.capellanesCircleMedium} sx={{ bottom: '15%', right: '10%' }} />

            {/* Card principal */}
            <Box className={classes.capellanesCard}>
                {/* Header dorado */}
                <Box className={classes.capellanesHeader}>
                    <Box className={classes.capellanesHeaderShine} />
                    <Box
                        component="img"
                        src={logo}
                        alt="Logo Alianza de Capellanes"
                        // className={classes.capellanesLogo}
                        sx={{
                            width: 100,
                            height: 'auto',
                            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                        }}
                    />
                    <Typography variant="h5" className={classes.capellanesTitle}>
                        Alianza de Capellanes
                    </Typography>
                    <Typography className={classes.capellanesSubtitle}>
                        Iniciar Sesión
                    </Typography>
                </Box>

                {/* Contenido del formulario */}
                <Box className={classes.capellanesContent}>
                    {/* Error general */}
                    {errors.general && (
                        <Box className={classes.capellanesAlertError}>
                            <Typography className={classes.capellanesAlertText}>
                                {errors.general}
                            </Typography>
                        </Box>
                    )}

                    <form onSubmit={handleLogin}>
                        <TextField
                            fullWidth
                            label="Correo Electrónico"
                            type="email"
                            variant="outlined"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (errors.email) setErrors({ ...errors, email: '' });
                            }}
                            error={!!errors.email}
                            helperText={errors.email}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email sx={{ color: capellanesColors.gold }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 3, ...textFieldCapellanes }}
                        />

                        <TextField
                            fullWidth
                            label="Contraseña"
                            type={showPassword ? 'text' : 'password'}
                            variant="outlined"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                if (errors.password) setErrors({ ...errors, password: '' });
                            }}
                            error={!!errors.password}
                            helperText={errors.password}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: capellanesColors.gold }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            sx={{ color: capellanesColors.lightGold }}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 4, ...textFieldCapellanes }}
                        />

                        {/* Botón de Login */}
                        <button
                            type="submit"
                            disabled={loading}
                            className={classes.capellanesButtonPrimary}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ color: capellanesColors.black }} />
                                    Iniciando sesión...
                                </>
                            ) : (
                                <>
                                    <Church sx={{ fontSize: 20 }} />
                                    Iniciar Sesión
                                </>
                            )}
                        </button>

                        {/* Divider */}
                        <Box className={classes.capellanesDivider}>
                            <Box className={classes.capellanesDividerLine} />
                            <Typography className={classes.capellanesDividerText}>
                                ¿Nuevo en la Alianza?
                            </Typography>
                            <Box className={classes.capellanesDividerLine} />
                        </Box>

                        {/* Botón de Registro */}
                        <button
                            type="button"
                            onClick={() => navigate('/registro')}
                            className={classes.capellanesButtonOutlined}
                        >
                            Únete a la Alianza
                        </button>

                        {/* Volver al inicio */}
                        <Box sx={{ textAlign: 'center', mt: 3 }}>
                            <span
                                onClick={() => navigate('/')}
                                className={classes.capellanesLink}
                            >
                                <ArrowBack sx={{ fontSize: 18 }} />
                                Volver al inicio
                            </span>
                        </Box>
                    </form>
                </Box>
            </Box>

            {/* CSS Animations (necesarias para los keyframes) */}
            <style>
                {`
                    @keyframes float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-15px); }
                    }
                `}
            </style>
        </Box>
    );
};

export default Login;